// Страница "Повторить сложные": загружает виртуальный вариант из предложений-ошибок
// и запускает StudentExercise с кастомными функциями завершения.

import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Container, Spinner, Card, Button } from 'react-bootstrap';
import AppNavbar from '../../components/common/AppNavbar';
import StudentExercise from '../../components/students/exercises/StudentExercise';
import { getRetryErrors, completeRetryErrors } from '../../api/studentApi';

export default function StudentRetryPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const typeId   = location.state?.typeId;
  const typeName = location.state?.typeName ?? '';

  const [phase, setPhase]             = useState('loading'); // loading | empty | error | exercise
  const [variantData, setVariantData] = useState(null);
  const [retryCount, setRetryCount]   = useState(0); // increment to re-trigger load

  useEffect(() => {
    if (!typeId) return;

    setPhase('loading');
    let cancelled = false;

    getRetryErrors(typeId)
      .then(({ data }) => {
        if (cancelled) return;
        const sentences = data?.sentences ?? {};
        if (Object.keys(sentences).length === 0) {
          setPhase('empty');
        } else {
          setVariantData(data);
          setPhase('exercise');
        }
      })
      .catch(() => {
        if (!cancelled) setPhase('error');
      });

    return () => { cancelled = true; };
  }, [typeId, retryCount]);

  // Если typeId не задан — редирект на главную студента
  if (!typeId) {
    return <Navigate to="/student" replace />;
  }

  // ── Кастомная функция завершения ────────────────────────────────────────────
  const handleCompleteVariantFn = async (_variantId, payload) => {
    const { data: result } = await completeRetryErrors(typeId, { answer: payload.answer });
    return { data: result };
  };

  // ── Кастомная навигация после завершения ────────────────────────────────────
  const handleResultNavigate = (result) => {
    navigate('/student/exercises/result', {
      state: {
        result,
        exerciseId: null,
        variantId: null,
        retryTypeId: typeId,
        retryTypeName: typeName,
      },
    });
  };

  // ── Фаза: загрузка ──────────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <>
        <AppNavbar />
        <div style={{ background: 'var(--ewl-bg-body)', minHeight: 'calc(100vh - 56px)' }}>
          <Container className="py-5 text-center" style={{ maxWidth: 480 }}>
            <Spinner
              animation="border"
              variant="primary"
              style={{ width: '2.5rem', height: '2.5rem', marginBottom: '1.25rem' }}
            />
            <p style={{ color: '#6c757d', fontSize: '0.95rem', margin: 0 }}>
              Подбираем предложения с ошибками...
            </p>
          </Container>
        </div>
      </>
    );
  }

  // ── Фаза: нет ошибок ────────────────────────────────────────────────────────
  if (phase === 'empty') {
    return (
      <>
        <AppNavbar />
        <div style={{ background: 'var(--ewl-bg-body)', minHeight: 'calc(100vh - 56px)' }}>
          <Container className="py-5" style={{ maxWidth: 520 }}>
            <Card
              className="border-0 text-center"
              style={{
                borderRadius: '1rem',
                boxShadow: '0 4px 24px rgba(0,0,0,0.09)',
                background: '#fff',
                overflow: 'hidden',
              }}
            >
              <div style={{ height: '0.375rem', background: 'linear-gradient(90deg, #28a745, #20c997)' }} />
              <Card.Body className="p-4 p-md-5">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
                <h5 className="fw-bold mb-3" style={{ color: '#1e2a4a' }}>
                  Отличная работа!
                </h5>
                <p style={{ color: '#6c757d', fontSize: '0.9rem', lineHeight: 1.7, marginBottom: '1.75rem' }}>
                  Нет предложений для повторения — в последних попытках вы не допускали ошибок
                  {typeName ? ` по типу «${typeName}»` : ''}.
                </p>
                <Button
                  onClick={() => navigate('/student')}
                  style={{
                    background: 'linear-gradient(135deg, #4f6ef7 0%, #7c3aed 100%)',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontWeight: 600,
                    padding: '0.55rem 1.5rem',
                  }}
                >
                  ← На главную
                </Button>
              </Card.Body>
            </Card>
          </Container>
        </div>
      </>
    );
  }

  // ── Фаза: ошибка загрузки ───────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <>
        <AppNavbar />
        <div style={{ background: 'var(--ewl-bg-body)', minHeight: 'calc(100vh - 56px)' }}>
          <Container className="py-5" style={{ maxWidth: 520 }}>
            <Card
              className="border-0 text-center"
              style={{
                borderRadius: '1rem',
                boxShadow: '0 4px 24px rgba(0,0,0,0.09)',
                background: '#fff',
                overflow: 'hidden',
              }}
            >
              <div style={{ height: '0.375rem', background: 'linear-gradient(90deg, #dc3545, #e55353)' }} />
              <Card.Body className="p-4 p-md-5">
                <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>⚠️</div>
                <h5 className="fw-bold mb-3" style={{ color: '#1e2a4a' }}>
                  Не удалось загрузить упражнение
                </h5>
                <p style={{ color: '#6c757d', fontSize: '0.9rem', lineHeight: 1.7, marginBottom: '1.75rem' }}>
                  Попробуйте ещё раз или вернитесь на главную.
                </p>
                <div className="d-flex justify-content-center gap-2">
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/student')}
                    style={{ borderRadius: '0.5rem', fontWeight: 500 }}
                  >
                    ← На главную
                  </Button>
                  <Button
                    onClick={() => setRetryCount((n) => n + 1)}
                    style={{
                      background: 'linear-gradient(135deg, #4f6ef7 0%, #7c3aed 100%)',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontWeight: 600,
                    }}
                  >
                    Попробовать снова
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Container>
        </div>
      </>
    );
  }

  // ── Фаза: упражнение ────────────────────────────────────────────────────────
  return (
    <StudentExercise
      variant={variantData}
      historyId={null}
      exerciseId={null}
      variantId={-1}
      navigate={navigate}
      onInterrupt={() => navigate('/student')}
      completeVariantFn={handleCompleteVariantFn}
      resultNavigateFn={handleResultNavigate}
    />
  );
}
